/**
 * Display Mode Color Palettes
 *
 * Three modes: dark, light, paperwhite (high contrast)
 * See docs/display-modes/display-modes-spec.md for details
 */

export const darkColors = {
  // Backgrounds (matching CSS --color-bg-* variables)
  bgPrimary: '#0f1a24',      // Main content area
  bgSecondary: '#162330',    // Sidebar, panels, session bar
  bgDeepest: '#060b10',      // Deepest sidebar background
  bgDeep: '#0a1118',         // Deep background
  bgElevated: '#162330',     // Cards, elevated surfaces
  bgSurface: '#1e2f40',      // Borders, dividers
  bgHover: '#253a4d',        // Hover states
  bgTertiary: '#1e2f40',     // Tertiary background

  // Text (including legacy aliases)
  textPrimary: '#f8f4e9',
  textSecondary: '#c4bfb0',
  textMuted: '#8a96a3',
  textDisabled: '#5c6a78',
  textDim: '#5c6a78',        // Alias for dim text
  text: '#f0ebe0',           // General text
  cream: '#f8f4e9',          // Light cream text
  creamMuted: '#c4bfb0',     // Muted cream text

  // Borders
  border: '#1e3044',
  borderMid: '#2a4058',
  borderLight: '#3a5068',

  // Sidebar
  sidebar: '#060b10',
  sidebarText: '#f8f4e9',
  sidebarTextMuted: '#8a96a3',
  sidebarHover: 'rgba(255, 255, 255, 0.05)',

  // Accents (same for text and backgrounds in dark mode)
  gold: '#f0c866',
  goldBg: '#f0c866',
  teal: '#4ecdc4',
  tealBg: '#4ecdc4',
  green: '#4ade80',
  greenBg: '#4ade80',
  coral: '#ff6b5b',
  purple: '#a78bfa',
  purpleBg: '#a78bfa',
  success: '#4ade80',
  warning: '#f0c866',
  danger: '#ff6b5b',

  // Planning section
  planningBg: '#1a2a38',
  planningBorder: '#2d4a5e',
  planningText: '#7eb8db',
  planningAccent: '#5b8fb3',
  planningHover: 'rgba(91, 143, 179, 0.1)',

  // Section headers
  seriesTint: 'rgba(240, 200, 102, 0.12)',
  seriesBorder: 'rgba(240, 200, 102, 0.25)',
  standaloneTint: 'rgba(78, 205, 196, 0.12)',
  standaloneBorder: 'rgba(78, 205, 196, 0.25)',

  // Messages
  userMessage: '#1e2f40',
  assistantMessage: '#162330',

  // Overlays and shadows
  overlay: 'rgba(6, 11, 16, 0.85)',
};

export const lightColors = {
  // Backgrounds - warm cream tones
  bgPrimary: '#faf8f3',      // Main background - warm off-white
  bgSecondary: '#f2efe6',    // Sidebar/panels - slightly darker cream
  bgDeepest: '#e8e4da',      // Deepest background (lighter in light mode)
  bgDeep: '#f2efe6',         // Deep background
  bgElevated: '#ffffff',     // Cards - pure white for lift
  bgSurface: '#e8e4da',      // Borders, dividers
  bgHover: '#eae6dc',        // Hover states
  bgTertiary: '#ffffff',     // Tertiary background

  // Text - DARK for contrast on light backgrounds
  textPrimary: '#1a1a18',
  textSecondary: '#3a3a35',
  textMuted: '#5a5a52',
  textDisabled: '#8a8a82',
  textDim: '#8a8a82',        // Alias for dim text
  text: '#1a1a18',           // General text
  cream: '#1a1a18',          // "Cream" is dark text in light mode
  creamMuted: '#3a3a35',     // Muted text

  // Borders
  border: '#e8e4da',
  borderMid: '#d8d4ca',
  borderLight: '#e8e4da',

  // Sidebar
  sidebar: '#f2efe6',
  sidebarText: '#1a1a18',
  sidebarTextMuted: '#5a5a52',
  sidebarHover: 'rgba(0, 0, 0, 0.05)',

  // Accents - split into text (darker) and background (lighter) variants
  gold: '#9a7a2a',           // Dark gold for TEXT on gold backgrounds
  goldBg: '#d4a84b',         // Lighter gold for icons/buttons
  teal: '#1a7a6f',           // Dark teal for TEXT on teal backgrounds
  tealBg: '#2a9d8f',         // Lighter teal for icons/buttons
  green: '#1a6a4a',          // Dark green for TEXT
  greenBg: '#2d8a5f',        // Lighter green for buttons
  coral: '#c84a3f',
  purple: '#5a3a9f',         // Dark purple for TEXT
  purpleBg: '#7c5cbf',       // Lighter purple for backgrounds
  success: '#1a6a4a',
  warning: '#9a7a2a',
  danger: '#c84a3f',

  // Planning section - blue tints
  planningBg: '#e8f1f8',
  planningBorder: '#a8c4d8',
  planningText: '#1a4a6a',   // Dark blue for readability
  planningAccent: '#2a6090',
  planningHover: 'rgba(42, 96, 144, 0.1)',

  // Section headers - colored tints with darker borders
  seriesTint: 'rgba(212, 168, 75, 0.18)',
  seriesBorder: 'rgba(154, 122, 42, 0.40)',
  standaloneTint: 'rgba(42, 157, 143, 0.15)',
  standaloneBorder: 'rgba(26, 122, 111, 0.35)',

  // Messages
  userMessage: '#e8f1f8',
  assistantMessage: '#ffffff',

  // Overlays and shadows
  overlay: 'rgba(0, 0, 0, 0.4)',
};

export const paperwhiteColors = {
  // Backgrounds - pure white, minimal gray
  bgPrimary: '#ffffff',
  bgSecondary: '#fafafa',
  bgDeepest: '#f0f0f0',      // Light gray in high contrast
  bgDeep: '#fafafa',         // Near white
  bgElevated: '#ffffff',
  bgSurface: '#909090',      // Dark gray for borders
  bgHover: '#f0f0f0',
  bgTertiary: '#ffffff',

  // Text - pure blacks, maximum contrast
  textPrimary: '#000000',
  textSecondary: '#000000',   // Same as primary
  textMuted: '#2a2a2a',
  textDisabled: '#505050',
  textDim: '#505050',
  text: '#000000',
  cream: '#000000',          // All text is black in high contrast
  creamMuted: '#2a2a2a',

  // Borders
  border: '#909090',
  borderMid: '#808080',
  borderLight: '#c0c0c0',

  // Sidebar
  sidebar: '#fafafa',
  sidebarText: '#000000',
  sidebarTextMuted: '#2a2a2a',
  sidebarHover: 'rgba(0, 0, 0, 0.08)',

  // Accents - ALL grayscale (no color on e-ink)
  gold: '#303030',
  goldBg: '#404040',
  teal: '#303030',
  tealBg: '#404040',
  green: '#303030',
  greenBg: '#404040',
  coral: '#303030',
  purple: '#303030',
  purpleBg: '#404040',
  success: '#303030',
  warning: '#303030',
  danger: '#303030',

  // Planning section - light bg, dark border
  planningBg: '#f5f5f5',
  planningBorder: '#606060',
  planningText: '#000000',
  planningAccent: '#404040',
  planningHover: 'rgba(0, 0, 0, 0.08)',

  // Section headers - light gray bg, dark borders (NO color)
  seriesTint: '#f0f0f0',
  seriesBorder: 'rgba(0, 0, 0, 0.4)',
  standaloneTint: '#f0f0f0',
  standaloneBorder: 'rgba(0, 0, 0, 0.4)',

  // Messages
  userMessage: '#f0f0f0',
  assistantMessage: '#ffffff',

  // Overlays and shadows
  overlay: 'rgba(0, 0, 0, 0.5)',
};

/**
 * Get colors for the specified display mode
 */
export function getColors(mode) {
  switch (mode) {
    case 'light':
      return lightColors;
    case 'paperwhite':
      return paperwhiteColors;
    case 'dark':
    default:
      return darkColors;
  }
}

/**
 * Check if current mode is a light-background mode (for shadows, etc.)
 */
export function isLightMode(mode) {
  return mode === 'light' || mode === 'paperwhite';
}

/**
 * Check if current mode is paperwhite (for special grayscale handling)
 */
export function isPaperwhiteMode(mode) {
  return mode === 'paperwhite';
}
